import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { downloadCsv, fmtNum } from "../lib/csv.ts";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { ForecastExplainer } from "../components/ForecastExplainer.tsx";
import {
  CoverSubtypeFilterRow,
  FilterPill,
  OutlineButton,
  type CoverSubtypeFilter,
} from "../components/FilterBar.tsx";
import { SearchInput } from "../components/SearchInput.tsx";
import { coverGroupRank, coverSortRank } from "../data/coverOrder.ts";
import { frameSortRank } from "../data/frameOrder.ts";
import { theme } from "../theme.ts";
import { ConfidenceBadge } from "../components/ConfidenceBadge.tsx";
import type { ForecastRow, ItemType, SafetyCalibration } from "@march/shared";
import { CATEGORY_LABEL, isYards } from "../lib/categories.ts";
import { useSearchParams } from "react-router-dom";

type FType = Extract<ItemType, "Frame" | "Cover" | "Base" | "Fabric" | "Trim" | "ProtectiveCover">;
interface ApiResp {
  type: string;
  rows: ForecastRow[];
  /** How this tab's safety buffers were sized. */
  safety?: SafetyCalibration[];
  generated_at: number;
}

/**
 * Flags a thin shelf. Triggered by on-hand against the 90-day forecast, so
 * it stays visible even when a PO is inbound — the point is that expediting
 * that PO is a decision someone has to make.
 */
function RiskBadge({
  kind,
  title,
}: {
  kind: "expedite" | "order";
  title: string;
}) {
  const c =
    kind === "expedite"
      ? { bg: theme.color.warningFaint, fg: theme.color.warningText, border: theme.color.warningBorder, label: "expedite?" }
      : { bg: theme.color.dangerFaint, fg: theme.color.dangerText, border: theme.color.dangerBorder, label: "order" };
  return (
    <span
      title={title}
      style={{
        marginLeft: 6,
        padding: "1px 6px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {c.label}
    </span>
  );
}

/** Stock on order more than 30 days late and not shipped. Flagged, still counted as supply. */
function StalledBadge({ r, unit }: { r: ForecastRow; unit: string }) {
  const pos = r.stalled_pos ?? [];
  return (
    <div
      title={`${fmtNum(Math.round(r.stalled_qty ?? 0))} ${unit} on PO ${pos.join(", ")} are more than 30 days past their expected date and not on a container. They still count toward Need to order. Check with the supplier — then fix the expected date in NetSuite, or close the line if it isn't coming.`}
      style={{
        display: "inline-block",
        marginTop: 2,
        padding: "0 6px",
        borderRadius: theme.radius.sm,
        background: theme.color.dangerFaint,
        color: theme.color.danger,
        fontSize: theme.font.size.xs,
        fontWeight: 600,
      }}
    >
      PO stalled · {fmtNum(Math.round(r.stalled_qty ?? 0))}
    </div>
  );
}

/** "300 on 2026-11-10 (container CA-26091) · 500 on 2027-05-16 (PO 16802)" — soonest first. */
function arrivalsText(r: ForecastRow, unit: string, max = 5): string {
  const a = r.arrivals ?? [];
  const parts = a.slice(0, max).map(
    (x) =>
      `${fmtNum(Math.round(x.qty))} ${x.date ? `on ${x.date}` : "with no date set"} (${x.source === "container" ? `container ${x.ref}` : `PO ${x.ref}`}${x.stalled ? ", stalled" : ""})`,
  );
  if (a.length > max) parts.push(`+${a.length - max} more`);
  return parts.length ? `${unit === "yards" ? "Yards" : "Units"} arriving: ${parts.join(" · ")}` : "";
}

export function ForecastDetail() {
  // Links can open a tab and filter: /forecast?type=Fabric&show=need|risk|stalled
  const [params] = useSearchParams();
  const FTYPES: FType[] = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"];
  const [type, setType] = useState<FType>(() => {
    const t = params.get("type") as FType | null;
    return t && FTYPES.includes(t) ? t : "Frame";
  });
  const [subtype, setSubtype] = useState<CoverSubtypeFilter>("All");
  const [search, setSearch] = useState("");
  // Show only rows where the shelf is thin. The trigger is on-hand vs the
  // 90-day forecast, so an item with a full container inbound still appears —
  // that is the point, since expediting it is a live option.
  const [riskOnly, setRiskOnly] = useState(() => params.get("show") === "risk");
  const [stalledOnly, setStalledOnly] = useState(() => params.get("show") === "stalled");
  const [needOnly, setNeedOnly] = useState(() => params.get("show") === "need");
  const q = useQuery<ApiResp>({
    queryKey: ["forecast", type],
    queryFn: () => apiGet<ApiResp>(`/api/forecast?type=${type}`),
  });

  useEffect(() => {
    if (type !== "Cover" && subtype !== "All") setSubtype("All");
  }, [type, subtype]);

  // Default (pre-click) ordering. Frames and Covers use March's
  // merchandising order (frameOrder.ts / coverOrder.ts) — product-family
  // sequences buyers read down, not anything derivable from the data.
  // Covers additionally group Olefin -> Pacifica -> Sunbrella, so the
  // subtype pills slice out a block that's already in the right order.
  // Bases keep most-at-risk-first. Fabric has no merchandising order and
  // most of its ~350 SKUs sell nothing, so it leads with what needs buying,
  // then the busiest. Either way, clicking a column header overrides this,
  // and a third click restores it.
  const sortedRows = useMemo(() => {
    if (!q.data?.rows) return [];
    const rows = [...q.data.rows];
    // Unlisted SKUs share MAX_SAFE_INTEGER, so alphabetize ties — the
    // tail stays stable and predictable rather than arbitrary.
    if (type === "Frame") {
      return rows.sort((a, b) => {
        const ar = frameSortRank(a.itemid);
        const br = frameSortRank(b.itemid);
        return ar !== br ? ar - br : a.itemid.localeCompare(b.itemid);
      });
    }
    if (type === "Cover") {
      return rows.sort((a, b) => {
        const ag = coverGroupRank(a.cover_subtype);
        const bg = coverGroupRank(b.cover_subtype);
        if (ag !== bg) return ag - bg;
        const ar = coverSortRank(a.itemid, a.cover_subtype);
        const br = coverSortRank(b.itemid, b.cover_subtype);
        return ar !== br ? ar - br : a.itemid.localeCompare(b.itemid);
      });
    }
    if (isYards(type)) {
      return rows.sort((a, b) => {
        const an = a.need_to_order ?? 0;
        const bn = b.need_to_order ?? 0;
        if (an !== bn) return bn - an;
        const af = a.demand.forecasted;
        const bf = b.demand.forecasted;
        return af !== bf ? bf - af : a.itemid.localeCompare(b.itemid);
      });
    }
    return rows.sort((a, b) => {
      const av = a.months_of_cover ?? Number.POSITIVE_INFINITY;
      const bv = b.months_of_cover ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
  }, [q.data, type]);

  const subtypeCounts = useMemo(() => {
    const c: Partial<Record<CoverSubtypeFilter, number>> = {
      All: sortedRows.length,
      Olefin: 0,
      Pacifica: 0,
      Sunbrella: 0,
    };
    for (const r of sortedRows) {
      if (r.cover_subtype) c[r.cover_subtype] = (c[r.cover_subtype] ?? 0) + 1;
    }
    return c;
  }, [sortedRows]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let out = sortedRows;
    if (type === "Cover" && subtype !== "All") {
      out = out.filter((r) => r.cover_subtype === subtype);
    }
    if (needle) {
      out = out.filter((r) => r.itemid.toLowerCase().includes(needle));
    }
    if (riskOnly) {
      out = out.filter((r) => r.stock_risk && r.stock_risk !== "none");
    }
    if (stalledOnly) {
      out = out.filter((r) => (r.stalled_qty ?? 0) > 0);
    }
    if (needOnly) {
      out = out.filter((r) => (r.need_to_order ?? 0) > 0);
    }
    return out;
  }, [sortedRows, search, type, subtype, riskOnly, stalledOnly, needOnly]);

  const needCount = useMemo(
    () => rows.filter((r) => (r.need_to_order ?? 0) > 0).length,
    [rows],
  );

  // The horizon is a rolling 90 days from today. The three prior-year
  // columns are actual demand in that same 90-day window, 1/2/3 years
  // back — the raw inputs the baseline averages. Label by year (a window
  // can straddle new year, but the start year is the clearest label).
  // Use the first row that actually HAS a window, not rows[0]: on the
  // Cover tab the first row is often a CoverBase with no forecast_current
  // row, which left the header reading "None..None".
  const win = q.data?.rows?.find((r) => r.window_from && r.window_to);
  const nowY = win?.window_from
    ? Number(win.window_from.slice(0, 4))
    : new Date().getUTCFullYear();
  const priorYrLabel = String(nowY - 1);
  const prior2YrLabel = String(nowY - 2);
  const prior3YrLabel = String(nowY - 3);
  const windowLabel =
    win?.window_from && win?.window_to
      ? `${win.window_from} → ${win.window_to}`
      : null;

  const riskCounts = useMemo(() => {
    const all = q.data?.rows ?? [];
    return {
      expedite: all.filter((r) => r.stock_risk === "expedite").length,
      order: all.filter((r) => r.stock_risk === "order").length,
      stalled: all.filter((r) => (r.stalled_qty ?? 0) > 0).length,
    };
  }, [q.data]);

  // "In built umbrellas" is a Frames/Covers-only reference: a frame or cover
  // inside an assembled umbrella is a whole, countable part. Fabric and trim
  // are sewn in and cannot be recovered, so those tabs do not get it.
  const isSplit = (r: ForecastRow) =>
    r.demand.model === "topdown" || r.demand.model === "topdown_lost" || r.demand.model === "pace";
  const showBuilt = type === "Frame" || type === "Cover";
  // Fabric is stocked and consumed by the yard; everything else by the piece.
  const unit = isYards(type) ? "yards" : "units";
  const yd = isYards(type) ? " (yd)" : "";
  // Safety buffer column shows on any tab whose category has a buffer.
  const safetyCalib = q.data?.safety ?? [];
  // Olefin and Pacifica covers are calibrated as their own category (imported).
  const safetyFor = (r: ForecastRow) => {
    const cat = r.item_type === "Cover" && /^C[^-]+-(F|FD|SA)\d/.test(r.itemid) ? "ImportedCover" : r.item_type;
    return safetyCalib.find((c) => c.item_type === cat && c.k > 0);
  };
  const showSafety = safetyCalib.some((c) => c.k > 0);
  const builtTitle = (r: ForecastRow) => {
    const src = r.supply.fg_sources ?? [];
    if (!src.length) return "None inside built umbrellas in stock at Jurupa + Texas.";
    return (
      `${fmtNum(r.supply.fg_available)} inside built umbrellas at Jurupa + Texas, mostly:\n` +
      src.map((x) => `  ${x.itemid}  ${fmtNum(x.qty)}`).join("\n") +
      "\n\nReference only — not in Total Supply or To Order. It helps only if these umbrellas sell."
    );
  };

  const columns: Column<ForecastRow>[] = [
    {
      key: "itemid",
      header: "SKU",
      render: (r) => (
        <>
          <b>{r.itemid}</b>
          {showBuilt && r.stock_risk && r.stock_risk !== "none" && r.supply.fg_available > 0 && (
            <div
              title={builtTitle(r)}
              style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted, marginTop: 2 }}
            >
              +{fmtNum(r.supply.fg_available)} inside built umbrellas
              {r.supply.comp_available + r.supply.fg_available >= r.demand.forecasted &&
                " — enough for 90 days if they sell"}
            </div>
          )}
          {r.stock_risk === "expedite" && (
            <RiskBadge
              kind="expedite"
              title={`On hand covers ${Math.round(r.days_cover_on_hand ?? 0)} days; next delivery in ${r.days_to_next_receipt ?? "?"} days — about ${r.days_exposed ?? "?"} days with an empty shelf. Stock is already on order, so the decision is whether to expedite it.`}
            />
          )}
          {r.stock_risk === "order" && (
            <RiskBadge
              kind="order"
              title={`On hand won't last the ${r.lead_days ?? 90}-day lead time (about ${Math.round(r.days_cover_on_hand ?? 0)} days of cover) and nothing is on order.`}
            />
          )}
        </>
      ),
      sortValue: (r) => r.itemid,
    },
    {
      key: "avail",
      header: `Comp Avail${yd}`,
      align: "right",
      render: (r) => fmtNum(r.supply.comp_available),
      sortValue: (r) => r.supply.comp_available,
    },
    ...(showBuilt
      ? [
          {
            key: "built",
            header: "In built umbrellas",
            align: "right" as const,
            render: (r: ForecastRow) =>
              r.item_type === "CoverBase" ? (
                <span style={{ color: theme.color.textFaint }}>—</span>
              ) : (
                <span title={builtTitle(r)} style={{ color: theme.color.textMuted }}>
                  {r.supply.fg_available > 0 ? fmtNum(r.supply.fg_available) : "0"}
                </span>
              ),
            sortValue: (r: ForecastRow) => r.supply.fg_available,
          },
        ]
      : []),
    {
      key: "po",
      header: `Comp PO${yd}`,
      align: "right",
      render: (r) => fmtNum(r.supply.comp_po),
      sortValue: (r) => r.supply.comp_po,
    },
    {
      // The date the PO actually lands. Total Supply counts every open PO
      // regardless of when it arrives, so without this a row can look
      // covered by stock that is months away.
      key: "arrives",
      header: "PO arrives",
      align: "right",
      sortValue: (r) => r.next_receipt_eta ?? "9999-99-99",
      render: (r) => {
        const undated = r.undated_po_qty ?? 0;
        if (!r.next_receipt_eta) {
          if (r.supply.comp_po > 0)
            // Stock IS on order — it just has no date. Saying "late" here
            // overstated lateness ~3x: most past-due dates were never set.
            return (
              <span
                title={`${fmtNum(r.supply.comp_po)} ${unit} on order, but no expected receipt date was ever entered in NetSuite — it was left equal to the PO date. Ask the supplier for an ETA.`}
                style={{ color: theme.color.textMuted, fontStyle: "italic" }}
              >
                No ETA
              </span>
            );
          return <span style={{ color: theme.color.textFaint }}>—</span>;
        }
        const d = r.days_to_next_receipt;
        const overdue = r.receipt_overdue === true;
        // Outside the window is a problem; already late is a bigger one.
        const late = !overdue && (d ?? 0) > (r.lead_days ?? 90);
        const title =
          (overdue
            ? `${fmtNum(Math.round(r.overdue_qty ?? 0))} ${unit} not yet shipped were expected by ${r.next_receipt_eta} and have not arrived.`
            : `Next arrival${d != null ? ` in ${d} days` : ""}${late ? `, after the ${r.lead_days}-day lead time` : ""}. Containers at sea use Synkio's ETA + 9 days; stock not yet shipped uses the PO's expected date.`) +
          "\n" +
          arrivalsText(r, unit);
        return (
          <span title={title}>
            <b style={{ color: overdue || late ? theme.color.danger : undefined }}>
              {r.next_receipt_eta}
            </b>
            {d != null && (
              <span
                style={{
                  color: overdue ? theme.color.danger : theme.color.textFaint,
                  fontSize: theme.font.size.xs,
                  marginLeft: 4,
                  fontWeight: overdue ? 600 : undefined,
                }}
              >
                {overdue ? `(${Math.abs(d)}d late)` : `(${d}d)`}
              </span>
            )}
            {(r.stalled_qty ?? 0) > 0 && <StalledBadge r={r} unit={unit} />}
            {undated > 0 && (
              <div
                title={`A further ${fmtNum(undated)} ${unit} on order have no expected receipt date set.`}
                style={{ color: theme.color.textFaint, fontSize: theme.font.size.xs, fontStyle: "italic" }}
              >
                +{fmtNum(undated)} no ETA
              </div>
            )}
          </span>
        );
      },
    },
    {
      // How long the shelf is empty before that delivery lands. This is the
      // number the expedite decision turns on.
      key: "exposed",
      header: "Days exposed",
      align: "right",
      sortValue: (r) => r.days_exposed ?? -1,
      render: (r) => {
        // An overdue delivery has no finite exposure — the stock should
        // already be here. Rendering a dash would read as "no risk", which is
        // exactly backwards for the most urgent rows on the page.
        if (r.receipt_overdue)
          return (
            <b
              title={`The delivery is late, so there is no arrival date to count toward. On-hand covers ${Math.round(r.days_cover_on_hand ?? 0)} days.`}
              style={{ color: theme.color.danger }}
            >
              PO late
            </b>
          );
        // No dated delivery to count toward: exposure is unknown, which is
        // different from "none" — don't render a dash that reads as safe.
        if (!r.next_receipt_eta && r.supply.comp_po > 0)
          return (
            <span
              title="Can't compute — none of the open POs has an expected receipt date set."
              style={{ color: theme.color.textMuted, fontStyle: "italic" }}
            >
              No ETA
            </span>
          );
        const v = r.days_exposed;
        if (v == null || v <= 0)
          return <span style={{ color: theme.color.textFaint }}>—</span>;
        return (
          <b
            title={`On-hand covers ${Math.round(r.days_cover_on_hand ?? 0)} days; the next delivery is ${r.days_to_next_receipt} days out. Roughly ${v} days with an empty shelf.`}
            style={{ color: theme.color.danger }}
          >
            {v}d
          </b>
        );
      },
    },
    {
      // Sits next to Comp PO because most of it is a slice OF Comp PO. Only
      // the unallocated part is additive, so the tooltip spells out the
      // split rather than letting the row read as Avail + PO + Water.
      key: "onwater",
      header: "On Water",
      align: "right",
      sortValue: (r) => r.on_water_qty ?? 0,
      render: (r) => {
        const v = r.on_water_qty ?? 0;
        if (v === 0)
          return <span style={{ color: theme.color.textFaint }}>—</span>;
        const unalloc = r.on_water_unallocated_qty ?? 0;
        const inPo = v - unalloc;
        const parts = [
          `${fmtNum(v)} ${unit} in transit`,
          r.on_water_eta ? `earliest ETA ${r.on_water_eta}` : null,
          inPo > 0 ? `${fmtNum(inPo)} already counted inside Comp PO` : null,
          unalloc > 0
            ? `${fmtNum(unalloc)} not on any PO — added to Total Supply`
            : null,
        ].filter(Boolean);
        return (
          <span title={parts.join(" · ")} style={{ color: theme.color.brand }}>
            {fmtNum(v)}
            {unalloc > 0 && (
              <span
                title={`Includes ${unit} with no PO allocation`}
                style={{ color: theme.color.warning }}
              >
                *
              </span>
            )}
            {r.on_water_eta && (
              <span
                style={{
                  color: theme.color.textFaint,
                  fontSize: theme.font.size.xs,
                  marginLeft: 4,
                }}
              >
                {r.on_water_eta.slice(5)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "supply",
      header: `Total Supply${yd}`,
      align: "right",
      sortValue: (r) => r.supply.total,
      render: (r) => (
        <b
          title="Comp Avail + Comp PO + any on-water not already on a PO"
          style={{ color: theme.color.brand }}
        >
          {fmtNum(r.supply.total)}
        </b>
      ),
    },
    {
      key: "prior3",
      header: `${prior3YrLabel} (90d)`,
      align: "right",
      sortValue: (r) => r.demand.prior3_yr,
      render: (r) => (
        <span
          title={`Actual demand in the same 90-day window, ${prior3YrLabel}`}
          style={{ color: theme.color.textFaint }}
        >
          {fmtNum(r.demand.prior3_yr)}
        </span>
      ),
    },
    {
      key: "prior2",
      header: `${prior2YrLabel} (90d)`,
      align: "right",
      sortValue: (r) => r.demand.prior2_yr,
      render: (r) => (
        <span
          title={`Actual demand in the same 90-day window, ${prior2YrLabel}`}
          style={{ color: theme.color.textMuted }}
        >
          {fmtNum(r.demand.prior2_yr)}
        </span>
      ),
    },
    {
      key: "prior1",
      header: `${priorYrLabel} (90d)`,
      align: "right",
      sortValue: (r) => r.demand.prior_yr,
      render: (r) => (
        <span
          title={`Actual demand in the same 90-day window, ${priorYrLabel}`}
        >
          {fmtNum(r.demand.prior_yr)}
        </span>
      ),
    },
    {
      key: "baseline",
      header: "Baseline = no-growth fcst",
      align: "right",
      sortValue: (r) => r.demand.baseline_avg,
      render: (r) => (
        <span
          title={
            `Average of the ${r.demand.baseline_years} prior-year windows` +
            (r.demand.baseline_years < 3
              ? " (fewer than 3 — demand history doesn't reach further back)"
              : "") +
            (isSplit(r)
              ? ". Shown for reference: this category now uses the top-down split, so this baseline does not drive the 90-day forecast."
              : ". This IS the forecast with no growth applied (×1.0) — the " +
                "reference the 90-day forecast column adjusts.")
          }
          style={isSplit(r) ? { color: theme.color.textFaint } : undefined}
        >
          {fmtNum(r.demand.baseline_avg)}
        </span>
      ),
    },
    {
      key: "growth",
      header: "Growth",
      align: "right",
      sortValue: (r) => r.demand.growth_multiplier,
      render: (r) => {
        const pct = r.demand.growth_multiplier * 100 - 100;
        const delta = Math.round(r.demand.forecasted - r.demand.baseline_avg);
        return (
          <span
            title={
              (r.demand.used_aggregate
                ? "Trailing-12-month YoY, aggregate fallback (this SKU is too low-volume for its own ratio)"
                : "Trailing-12-month YoY for this SKU (last 12 complete months ÷ the 12 before)") +
              (isSplit(r)
                ? " · shown for reference; the top-down split uses the category's growth instead"
                : ` · moves the forecast ${delta >= 0 ? "+" : ""}${fmtNum(delta)} vs the no-growth baseline`)
            }
            style={{
              color: isSplit(r) || r.demand.used_aggregate
                ? theme.color.textFaint
                : pct < 0
                  ? theme.color.danger
                  : theme.color.text,
            }}
          >
            {pct > 0 ? "+" : ""}
            {pct.toFixed(0)}%{r.demand.used_aggregate && "*"}
          </span>
        );
      },
    },
    {
      key: "fcst",
      header: `90-day forecast${yd}`,
      align: "right",
      sortValue: (r) => r.demand.forecasted,
      render: (r) =>
        isSplit(r) ? (
          <span
            title={
              `Top-down split: the ${r.item_type === "CoverBase" ? "cover base" : r.item_type.toLowerCase()} ` +
              `category's 90-day forecast × this SKU's share of the category's last 12 months` +
              (r.demand.model === "topdown_lost"
                ? `, both counting orders that were cancelled or closed unshipped as demand`
                : "") +
              (r.demand.model === "pace" && r.demand.pace_mult != null
                ? `, × ${r.demand.pace_mult.toFixed(2)} for the category's pace — its last 28 days ran ${
                    r.demand.pace_mult >= 1 ? "above" : "below"
                  } forecast, and the forecast moves halfway toward that`
                : "") +
              (r.demand.split_share != null ? ` (${(r.demand.split_share * 100).toFixed(1)}%)` : "") +
              `.` +
              (r.demand.formula_qty != null
                ? ` The same-window formula would say ${fmtNum(r.demand.formula_qty)}.`
                : "") +
              ` Chosen because it beat the formula in the backtest — see the Accuracy page.`
            }
          >
            {fmtNum(r.demand.forecasted)}
            <span style={{ marginLeft: 4, fontSize: 9, color: theme.color.textFaint, fontWeight: 600 }}>
              {r.demand.model === "topdown_lost" ? "SPLIT+" : r.demand.model === "pace" ? "SPLIT·PACE" : "SPLIT"}
            </span>
          </span>
        ) : (
          fmtNum(r.demand.forecasted)
        ),
    },
    {
      // How past forecasts for this SKU compared with what sold.
      key: "conf",
      header: "Confidence",
      render: (r) => <ConfidenceBadge grade={r.confidence} hitRate={r.confidence_hit_rate} />,
      sortValue: (r) =>
        r.confidence ? ["high", "medium", "low", "low_volume", "new"].indexOf(r.confidence) : null,
    },
    {
      key: "moc",
      header: "Months of cover",
      align: "right",
      sortValue: (r) => r.months_of_cover,
      render: (r) => {
        if (r.months_of_cover === null) return "∞";
        const v = r.months_of_cover;
        const c =
          v < 1
            ? theme.color.danger
            : v < 3
              ? theme.color.warning
              : theme.color.success;
        return <b style={{ color: c }}>{v.toFixed(1)}</b>;
      },
    },
    {
      // Need-to-order is judged over the lead time, not the 90-day window:
      // ordering is on demand, so stock + on-order only has to last until a
      // new order could land (data/lead_times.ts).
      key: "lead",
      header: `Lead-time demand${yd}`,
      align: "right",
      sortValue: (r) => r.lead_forecast ?? null,
      render: (r) =>
        r.lead_forecast == null ? (
          "—"
        ) : (
          <span
            title={`Forecast demand over the next ${r.lead_days} days — this SKU's lead time from ordering to usable stock. Need to order and the order/expedite tags are judged against this, not the 90-day forecast.`}
          >
            {fmtNum(Math.round(r.lead_forecast))}
            <span style={{ marginLeft: 4, fontSize: 10, color: theme.color.textFaint }}>
              {r.lead_days}d
            </span>
          </span>
        ),
    },
    ...(showSafety
      ? [
          {
            // Extra stock against a busier-than-forecast lead time.
            key: "safety",
            header: "Safety buffer",
            align: "right" as const,
            sortValue: (r: ForecastRow) => r.safety_qty ?? 0,
            render: (r: ForecastRow) => {
              const c = safetyFor(r);
              if (!c || !(r.safety_qty && r.safety_qty > 0)) return <span style={{ color: theme.color.textFaint }}>—</span>;
              return (
                <span
                  title={
                    `Extra stock so that ${
                      c.mode === "cycle"
                        ? `the shelf would not have run out in ${Math.round(c.target * 100)}% of past ${r.lead_days ?? 90}-day windows`
                        : `${Math.round(c.target * 100)}% of demand would have shipped from stock`
                    }. This SKU's forecasts have been off by about ${Math.round((r.safety_rel ?? 0) * 100)}%; ` +
                    `buffer = ${c.k.toFixed(2)} × ${Math.round((r.safety_rel ?? 0) * 100)}% × lead-time demand. Admins set the target on the Admin page.`
                  }
                >
                  {fmtNum(Math.round(r.safety_qty))}
                </span>
              );
            },
          },
        ]
      : []),
    {
      key: "need",
      header: `Need to order${yd}`,
      align: "right",
      sortValue: (r) => r.need_to_order,
      render: (r) => {
        if (r.need_to_order == null)
          return (
            <span
              title="Supply isn't mapped for this SKU type, so a buy qty can't be computed"
              style={{ color: theme.color.textFaint }}
            >
              —
            </span>
          );
        const v = r.need_to_order;
        if (v === 0) {
          // Zero has two very different meanings, and the tooltip used to
          // claim "covered" for both — reading "Covered — -50 units above"
          // on a discontinued frame that is genuinely short.
          const retiring = r.lifecycle && r.lifecycle !== "active";
          const gap = Math.round(r.supply.total - (r.lead_forecast ?? r.demand.forecasted));
          const title = retiring
            ? `Not reordered — the plan is retiring this SKU (${r.lifecycle === "sell_through" ? "sell through, then discontinue" : "discontinued"}).${gap < 0 ? ` Forecast exceeds supply by ${fmtNum(-gap)}; that is the intended run-down.` : ""}`
            : r.stock_risk === "expedite"
              ? `Nothing more to buy — ${fmtNum(Math.round(r.supply.incoming_within_lead ?? r.supply.comp_po))} arriving within the lead time. Shelf is thin now; see Days exposed.`
              : `Covered — on hand plus stock arriving within the ${r.lead_days ?? 90}-day lead time covers demand and buffer`;
          return (
            <span title={title} style={{ color: theme.color.textFaint }}>
              —
            </span>
          );
        }
        // Same pill treatment as Monitor's Suggested PO — it's the same
        // kind of "here's the action" number. Mirror the exact route
        // formula: forecast − avail − on-order − on-water-not-on-a-PO.
        const later = Math.round(r.supply.incoming_after_lead ?? 0);
        const title =
          `Demand over the ${r.lead_days ?? 90}-day lead time ${fmtNum(Math.round(r.lead_forecast ?? r.demand.forecasted))}` +
          ((r.safety_qty ?? 0) >= 0.5 ? ` + safety buffer ${fmtNum(Math.round(r.safety_qty!))}` : "") +
          ` − on hand ${fmtNum(r.supply.comp_available)}` +
          ` − arriving within the lead time ${fmtNum(Math.round(r.supply.incoming_within_lead ?? 0))}` +
          ` = order ${fmtNum(v)}` +
          (later > 0
            ? `\n${fmtNum(later)} more ${unit} are on order but land after the lead time — consider asking the supplier to bring a release forward rather than ordering new.`
            : "") +
          ((r.stalled_qty ?? 0) > 0
            ? `\n⚠ ${fmtNum(Math.round(r.stalled_qty!))} of the stock on order is over 30 days late (PO ${(r.stalled_pos ?? []).join(", ")}) and still counted — if it isn't coming, Need to order is too low.`
            : "") +
          (r.arrivals?.length ? `\n${arrivalsText(r, unit)}` : "");
        return (
          <b
            title={title}
            style={{
              background: theme.color.dangerFaint,
              color: theme.color.danger,
              padding: "2px 8px",
              borderRadius: theme.radius.sm,
            }}
          >
            {fmtNum(v)}
          </b>
        );
      },
    },
  ];

  const exportCsv = () => {
    downloadCsv(
      `forecast-${type.toLowerCase()}s-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((r) => ({
        SKU: r.itemid,
        Type: r.item_type,
        Unit: unit,
        Cover_subtype: r.cover_subtype ?? "",
        Comp_available: r.supply.comp_available,
        ...(showBuilt ? { In_built_umbrellas: r.supply.fg_available } : {}),
        Comp_PO: r.supply.comp_po,
        PO_arrives: r.next_receipt_eta ?? "",
        PO_days_away: r.days_to_next_receipt ?? "",
        Days_cover_on_hand:
          r.days_cover_on_hand == null ? "" : Math.round(r.days_cover_on_hand),
        Days_exposed: r.days_exposed ?? "",
        PO_overdue: r.receipt_overdue ? "Y" : "",
        PO_stalled_qty: Math.round(r.stalled_qty ?? 0),
        PO_stalled: (r.stalled_pos ?? []).join(" "),
        [isYards(type) ? "PO_yards_no_ETA" : "PO_units_no_ETA"]: r.undated_po_qty ?? 0,
        Stock_risk: r.stock_risk ?? "",
        On_water: r.on_water_qty ?? 0,
        On_water_unallocated: r.on_water_unallocated_qty ?? 0,
        On_water_eta: r.on_water_eta ?? "",
        Total_supply: r.supply.total,
        Lead_days: r.lead_days ?? "",
        Lead_time_demand: r.lead_forecast == null ? "" : Math.round(r.lead_forecast),
        Safety_buffer: Math.round(r.safety_qty ?? 0),
        Confidence: r.confidence ?? "",
        Forecast_model:
          r.demand.model === "pace"
            ? "top-down split × category pace"
            : r.demand.model === "topdown_lost"
            ? "top-down split + cancelled orders"
            : r.demand.model === "topdown"
              ? "top-down split"
              : "same-window formula",
        Formula_forecast: r.demand.formula_qty == null ? "" : Math.round(r.demand.formula_qty),
        [`Demand_90d_${prior3YrLabel}`]: Math.round(r.demand.prior3_yr),
        [`Demand_90d_${prior2YrLabel}`]: Math.round(r.demand.prior2_yr),
        [`Demand_90d_${priorYrLabel}`]: Math.round(r.demand.prior_yr),
        Baseline_90d: Math.round(r.demand.baseline_avg),
        Baseline_years: r.demand.baseline_years,
        Growth_multiplier: r.demand.growth_multiplier.toFixed(3),
        Aggregate_fallback: r.demand.used_aggregate ? "Y" : "N",
        Forecasted_90d: Math.round(r.demand.forecasted),
        Window_from: r.window_from ?? "",
        Window_to: r.window_to ?? "",
        Months_of_cover:
          r.months_of_cover === null
            ? "infinite"
            : r.months_of_cover.toFixed(2),
        Shortfall_surplus:
          r.shortfall == null ? "n/a" : Math.round(r.shortfall),
        Need_to_order: r.need_to_order == null ? "n/a" : r.need_to_order,
      })),
    );
  };

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <ForecastExplainer />

      <div
        style={{
          display: "flex",
          gap: theme.space.sm,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {(["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"] as FType[]).map((t) => (
          <FilterPill key={t} value={t} active={type === t} onClick={setType}>
            {CATEGORY_LABEL[t]}
          </FilterPill>
        ))}
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search SKU…"
          ariaLabel="Search SKUs"
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: theme.font.size.sm,
            color: theme.color.textMuted,
            cursor: "pointer",
          }}
          title="On-hand stock that won't last the SKU's lead time (the order / expedite? tags). Open POs are ignored on purpose — stock arriving later does not cover the shelf now."
        >
          <input
            type="checkbox"
            checked={riskOnly}
            onChange={(e) => setRiskOnly(e.target.checked)}
          />
          Low on-hand only
          {riskCounts.expedite + riskCounts.order > 0 && (
            <span style={{ color: theme.color.textFaint }}>
              ({riskCounts.expedite} expedite? · {riskCounts.order} order)
            </span>
          )}
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: theme.font.size.sm,
            color: theme.color.textMuted,
            cursor: "pointer",
          }}
          title="Only rows with a positive Need to order"
        >
          <input type="checkbox" checked={needOnly} onChange={(e) => setNeedOnly(e.target.checked)} />
          Need to order only
        </label>
        {riskCounts.stalled > 0 && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: theme.font.size.sm,
              color: theme.color.danger,
              cursor: "pointer",
            }}
            title="Stock on order whose expected date passed more than 30 days ago and isn't on a container yet. Still counted as supply — chase the supplier, or fix the date in NetSuite, or close the line if it isn't coming."
          >
            <input type="checkbox" checked={stalledOnly} onChange={(e) => setStalledOnly(e.target.checked)} />
            Stalled POs ({riskCounts.stalled})
          </label>
        )}
        <div style={{ marginLeft: "auto" }}>
          <OutlineButton onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV
          </OutlineButton>
        </div>
      </div>

      {type === "Cover" && (
        <CoverSubtypeFilterRow
          value={subtype}
          onChange={setSubtype}
          counts={subtypeCounts}
        />
      )}

      {q.isLoading && (
        <div style={{ color: theme.color.textMuted }}>Loading…</div>
      )}
      {q.error && (
        <div
          style={{
            padding: theme.space.md,
            background: theme.color.dangerFaint,
            color: theme.color.danger,
            borderRadius: theme.radius.md,
          }}
        >
          {(q.error as Error).message}
        </div>
      )}

      {q.data && (
        <>
          <div
            style={{
              fontSize: theme.font.size.sm,
              color: theme.color.textMuted,
            }}
          >
            <b style={{ color: theme.color.text }}>{rows.length}</b> SKUs
            {isYards(type) && (
              <>
                {" · "}
                <b style={{ color: theme.color.text }}>quantities in yards</b>
              </>
            )}
            {needCount > 0 && (
              <>
                {" · "}
                <b style={{ color: theme.color.danger }}>
                  {needCount} need ordering
                </b>
              </>
            )}
            {windowLabel && (
              <>
                {" · "}
                <span title="Rolling 90-day forecast window, starting today">
                  90 days: <b style={{ color: theme.color.text }}>{windowLabel}</b>
                </span>
              </>
            )}
            {" · "}* = growth from aggregate fallback (low volume) ·{" "}
            <span style={{ color: theme.color.textFaint }}>
              click a header to sort, drag to reorder
            </span>
          </div>
          {/* One storageKey for every tab — they share the same columns, so a
              reorder on Frames should carry over to Covers/Bases/Fabric. */}
          <DataTable
            storageKey="forecast"
            columns={columns}
            rows={rows}
            emptyMessage={
              search.trim()
                ? `No SKUs match "${search.trim()}".`
                : "No forecast data yet — waiting on backfill to complete."
            }
          />
        </>
      )}
    </div>
  );
}
